import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Heart } from "lucide-react";
import SectionEyebrow from "@/components/site/SectionEyebrow";

const ACUITY = "/book";

const HERO_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/2d0f827f-f90e-4e2b-b2d6-2ec95ecd7620/1694719394667.jpg";
const WORKSHOP_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/943f15c8-bbcd-4b08-9d02-af1b69757a92/wbcgboro+doingthework.jpg";
const FOUNDER_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/db9df231-7ec5-4a7f-90ec-ee5b58d79a2a/0M3A4752.jpg";
const CONNECTION_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/1769033455508-OD1Q4PI2JDAU3XUPCKBH/unsplash-image-ETRPjvb0KM0.jpg";
const WMWE_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/66f23618-666b-435c-8e09-4c16f41965ca/WMWE+Brunch+2023.png";

const carePillars = [
    {
        letter: "C",
        title: "Confidence",
        body: "Know who you are and how you show up. We build interior fluency before exterior strategy.",
    },
    {
        letter: "A",
        title: "Alignment",
        body: "Connect your values to actions and people. Mission, message, and method moving in concert.",
    },
    {
        letter: "R",
        title: "Relationships",
        body: "Build trust through communication and empathy. Networks become ecosystems, not transactions.",
    },
    {
        letter: "E",
        title: "Execution",
        body: "Turn connection into consistent, measurable results. Architecture, systems, and follow-through.",
    },
];

const services = [
    { name: "9-Module Curriculum", body: "A year-long, structured infrastructure that builds social capital at scale." },
    { name: "High-Impact Workshops", body: "Scenario-based activations on networking, communication, and presence." },
    { name: "Keynotes & Campus Events", body: "High-energy experiences that ignite culture and spark connection." },
    { name: "Faculty & Staff Training", body: "Equip educators to model and sustain connection-centered culture." },
    { name: "Custom Partnership Packages", body: "Multi-year strategy, retreats, and integration of the C.A.R.E.&trade; framework." },
];

const audiences = [
    "Universities",
    "Community Colleges",
    "K-12 & CTE Programs",
    "Workforce Development Agencies",
];

const testimonials = [
    {
        quote: "Shana is a breath of fresh air and her passion is contagious. Her perspective on networking shifts the entire concept of relationship building with positive outcomes. She cares deeply about the next generation and is an amazing community leader who has led a journey of impact and honesty.",
        name: "Jania Massey",
        role: "Stiletto Boss University / Huepact Consulting",
    },
    {
        quote: "I wanted to extend my heartfelt gratitude for your insightful presentation to the business students. The information you shared was truly profound, and I genuinely learned a lot from it. Your expertise and perspective added immense value to the session. I'm excited about the prospect of collaborating with you again in the future.",
        name: "Dr. Kemi Elufiede",
        role: "Remington College",
    },
    {
        quote: "Great tips. I loved Shana's authenticity. She did a great job of using members of audience to reinforce points. Great job!",
        name: "Ebony Stubbs",
        role: "Rae Images",
    },
    {
        quote: "Shana is the person that can Empower, Elevate and Excel you in building relationships.",
        name: "Tanya Dial-Bethune",
        role: "VP / Regional Manager, Self-Help Credit Union",
    },
];

export default function Home() {
    return (
        <main className="bg-[#fbf9f8]" data-testid="home-page">
            {/* HERO */}
            <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 relative z-10">
                        <p className="font-serif italic text-base md:text-lg text-[#0c0f3d]/65 mb-6 max-w-xl leading-snug">
                            We partner with universities and mission&#8209;driven institutions to architect connection&#8209;centered ecosystems.
                        </p>
                        <h1 className="font-serif text-[48px] sm:text-7xl lg:text-[104px] leading-[0.92] tracking-tight text-[#0c0f3d]">
                            Connection is not a soft skill.
                            <span className="block italic text-[#e47a6a]">It's infrastructure.</span>
                        </h1>
                        <p className="font-sans text-base md:text-lg text-[#0c0f3d]/75 max-w-xl mt-8 leading-relaxed">
                            Designing the systems, frameworks, and experiences that expand networks, deepen trust, and move missions forward — for universities, community colleges, K&#8209;12 programs, and workforce development agencies.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to={ACUITY}
                                data-testid="hero-book-call-btn"
                                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] transition-colors duration-300"
                            >
                                Book a Strategy Call
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-explore-services-btn"
                                className="group inline-flex items-center gap-3 px-7 py-4 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-[#fbf9f8] transition-colors duration-300"
                            >
                                Explore Services
                                <ArrowUpRight size={14} />
                            </Link>
                        </div>

                        <div className="mt-16 hairline max-w-md" />
                        <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#0c0f3d]/60 mt-6">
                            Trusted by institutions, leaders &amp; boards across the Carolinas and beyond
                        </p>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                                src={HERO_IMG}
                                alt="Shana Price leading a workshop"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0c0f3d]/30 via-transparent" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 md:-bottom-10 md:-left-10 bg-[#0c0f3d] text-[#fbf9f8] px-7 py-6 max-w-[260px]">
                            <p className="font-serif text-3xl leading-tight italic">"We architect systems, not sessions."</p>
                            <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#ffb5ab] mt-3">— Shana Price</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHO WE SERVE — Marquee */}
            <section className="border-y border-[#e3c7b9] bg-[#fbf9f8] overflow-hidden py-7">
                <div className="flex gap-16 animate-marquee whitespace-nowrap">
                    {[...audiences, ...audiences, ...audiences, ...audiences].map((a, i) => (
                        <span key={i} className="font-serif text-3xl md:text-4xl italic text-[#0c0f3d]/70">
                            {a} <span className="text-[#e47a6a] not-italic mx-6">&#10038;</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* WHO WE SERVE — Detail */}
            <section className="py-24 md:py-32 bg-[#fbf9f8]" data-testid="who-we-serve-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-7">
                            <SectionEyebrow>Who We Serve</SectionEyebrow>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1] tracking-tight text-[#0c0f3d]">
                                Designed for institutions
                                <span className="block italic text-[#e47a6a]">advancing pathways &amp; people.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#0c0f3d]/15">
                        <div className="bg-[#fbf9f8] p-10 md:p-12">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-4">Primary Partners</p>
                            <h3 className="font-serif text-2xl md:text-3xl text-[#0c0f3d] mb-3 leading-tight">
                                Education institutions committed to strengthening culture, belonging, and readiness.
                            </h3>
                            <ul className="mt-6 space-y-2 font-serif text-2xl md:text-3xl italic text-[#0c0f3d]/80">
                                <li><span className="text-[#e47a6a] not-italic mr-3">&#10038;</span>Universities</li>
                                <li><span className="text-[#e47a6a] not-italic mr-3">&#10038;</span>Community Colleges</li>
                                <li><span className="text-[#e47a6a] not-italic mr-3">&#10038;</span>K&#8209;12 &amp; CTE Programs</li>
                            </ul>
                        </div>
                        <div className="bg-[#fbf9f8] p-10 md:p-12">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-4">Mission&#8209;Aligned Partners</p>
                            <h3 className="font-serif text-2xl md:text-3xl text-[#0c0f3d] mb-3 leading-tight">
                                Organizations advancing pathways, opportunity, and workforce readiness.
                            </h3>
                            <ul className="mt-6 space-y-2 font-serif text-2xl md:text-3xl italic text-[#0c0f3d]/80">
                                <li><span className="text-[#e47a6a] not-italic mr-3">&#10038;</span>Workforce Development Agencies</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* C.A.R.E. FRAMEWORK */}
            <section className="py-24 md:py-36 bg-[#0c0f3d] text-[#fbf9f8] relative overflow-hidden" data-testid="care-framework-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 md:mb-24">
                        <div className="lg:col-span-5">
                            <SectionEyebrow color="#ffb5ab">Proprietary Methodology</SectionEyebrow>
                            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                                The <span className="italic text-[#ffb5ab]">C.A.R.E.</span><sup className="text-base align-super">&trade;</sup> Framework.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                            <p className="font-sans text-base md:text-lg text-[#e3c7b9]/80 leading-relaxed">
                                The intellectual property behind every curriculum, workshop, and partnership we deliver.
                                Four pillars that turn relational instinct into measurable institutional outcomes.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#6d91ad]/20">
                        {carePillars.map((p, i) => (
                            <div
                                key={p.title}
                                className="group bg-[#0c0f3d] p-10 md:p-12 hover:bg-[#0c0f3d]/40 transition-colors duration-700"
                                data-testid={`care-pillar-${p.title.toLowerCase()}`}
                            >
                                <div className="flex items-baseline gap-4 mb-8">
                                    <span className="font-serif italic text-[#e47a6a] text-7xl md:text-8xl leading-none">
                                        {p.letter}
                                    </span>
                                    <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad]">
                                        0{i + 1}
                                    </span>
                                </div>
                                <h3 className="font-serif text-3xl mb-5">{p.title}</h3>
                                <p className="font-sans text-sm text-[#e3c7b9]/70 leading-relaxed">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THE PROBLEM + SOLUTION SPLIT */}
            <section className="py-24 md:py-36 bg-[#fbf9f8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <img
                                src={WORKSHOP_IMG}
                                alt="Workshop in action"
                                className="w-full aspect-[4/5] object-cover"
                            />
                            <div className="absolute -right-4 -bottom-4 bg-[#e3c7b9] px-6 py-5 max-w-[230px]">
                                <p className="font-serif text-xl italic text-[#0c0f3d] leading-tight">
                                    Real students. Real connection. Real growth.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <SectionEyebrow>The Problem We Solve</SectionEyebrow>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#0c0f3d] mb-8">
                            Capable people, increasingly <span className="italic text-[#e47a6a]">disconnected</span> from the relationships that shape opportunity.
                        </h2>
                        <p className="font-sans text-base md:text-lg text-[#0c0f3d]/75 leading-relaxed mb-8">
                            Institutions, leadership teams, and boards see the impact in declining confidence, fragile communication, and limited access to opportunity. These aren't people problems &mdash; they're connection-system gaps.
                        </p>
                        <ul className="space-y-3 font-sans text-base text-[#0c0f3d]/80">
                            {[
                                "Declining confidence and communication fluency",
                                "Limited engagement with mentors, employers, and stakeholders",
                                "Reduced access to opportunity, retention, and belonging",
                                "Fragmented culture across departments and cohorts",
                            ].map((line) => (
                                <li key={line} className="flex items-start gap-3">
                                    <span className="text-[#e47a6a] font-serif italic text-xl leading-tight">&#10038;</span>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="py-24 md:py-36 bg-[#e3c7b9]/40 relative" data-testid="services-preview-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-7">
                            <SectionEyebrow>Our Solution</SectionEyebrow>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1] tracking-tight text-[#0c0f3d]">
                                A connection-centered ecosystem,
                                <span className="block italic text-[#e47a6a]">built to scale.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 flex items-end">
                            <p className="font-sans text-base text-[#0c0f3d]/75 leading-relaxed">
                                Five core engagements. One unified architecture. Each delivered with research-grounded rigor and high-energy facilitation.
                            </p>
                        </div>
                    </div>

                    <div className="border-t border-[#0c0f3d]/20">
                        {services.map((s, i) => (
                            <Link
                                key={s.name}
                                to="/services"
                                data-testid={`service-row-${i}`}
                                className="group block border-b border-[#0c0f3d]/20 py-8 md:py-10 hover:bg-[#fbf9f8]/60 transition-colors"
                            >
                                <div className="grid grid-cols-12 gap-6 items-center">
                                    <span className="col-span-1 font-sans text-xs text-[#0c0f3d]/50 tracking-widest">
                                        0{i + 1}
                                    </span>
                                    <h3 className="col-span-10 md:col-span-5 font-serif text-2xl md:text-4xl text-[#0c0f3d] tracking-tight">
                                        {s.name}
                                    </h3>
                                    <p className="hidden md:block col-span-5 font-sans text-sm text-[#0c0f3d]/75 leading-relaxed">
                                        {s.body}
                                    </p>
                                    <span className="col-span-1 flex justify-end text-[#0c0f3d] group-hover:text-[#e47a6a] group-hover:translate-x-1 transition-all">
                                        <ArrowUpRight size={22} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className="mt-12">
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-3 px-7 py-4 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] transition-colors duration-300"
                            data-testid="services-view-all-btn"
                        >
                            View All Partnership Packages
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-24 md:py-36 bg-[#fbf9f8]" data-testid="testimonials-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-7">
                            <SectionEyebrow>What Partners Say</SectionEyebrow>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1] tracking-tight text-[#0c0f3d]">
                                Quiet results.
                                <span className="block italic text-[#e47a6a]">Loud testimony.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
                        {testimonials.map((t, i) => (
                            <article
                                key={i}
                                data-testid={`testimonial-${i}`}
                                className="relative pt-12"
                            >
                                <span className="font-serif text-[120px] leading-none text-[#e47a6a] absolute -top-2 -left-3 select-none">
                                    &ldquo;
                                </span>
                                <p className="font-serif text-xl md:text-[22px] leading-[1.3] text-[#0c0f3d] relative">
                                    {t.quote}
                                </p>
                                <div className="mt-8 pt-6 border-t border-[#b59b7c]/40">
                                    <p className="font-sans text-sm font-semibold text-[#0c0f3d]">{t.name}</p>
                                    <p className="font-sans text-xs uppercase tracking-[0.18em] text-[#6d91ad] mt-1">{t.role}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* FOUNDER BIO PREVIEW */}
            <section className="py-24 md:py-36 bg-[#0c0f3d] text-[#fbf9f8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5 relative">
                        <img
                            src={FOUNDER_IMG}
                            alt="Shana Price"
                            className="w-full aspect-[4/5] object-cover"
                        />
                        <div className="absolute -bottom-5 -right-5 bg-[#ffb5ab] text-[#0c0f3d] px-6 py-4">
                            <p className="font-serif text-xl italic">Founder &amp; Architect</p>
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <SectionEyebrow color="#ffb5ab">About Shana</SectionEyebrow>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight">
                            Meet the architect <span className="italic text-[#ffb5ab]">behind the movement.</span>
                        </h2>
                        <p className="font-sans text-base md:text-lg text-[#e3c7b9]/80 leading-relaxed mt-8">
                            Shana Price is the Founder of The Connection Guru&reg; and creator of the C.A.R.E.&trade; Framework. With 30+ years of service-driven leadership, she partners with institutions and leaders to build the systems, structures, and experiences that strengthen success through connection.
                        </p>
                        <p className="font-sans text-base text-[#e3c7b9]/70 leading-relaxed mt-5">
                            Her work blends research-based frameworks, culturally responsive teaching, and high-energy facilitation &mdash; creating transformative ecosystems where people connect with confidence, communicate with clarity, and build relationships that open doors.
                        </p>
                        <Link
                            to="/about"
                            data-testid="founder-bio-link"
                            className="group inline-flex items-center gap-3 mt-10 px-7 py-4 border border-[#ffb5ab] text-[#ffb5ab] text-xs uppercase tracking-[0.2em] hover:bg-[#ffb5ab] hover:text-[#0c0f3d] transition-colors duration-300"
                        >
                            Read Full Bio
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* SACRED WORK — WMWE HIGHLIGHT */}
            <section className="py-24 md:py-36 bg-sanct-sand" data-testid="sacred-work-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <img
                                src={WMWE_IMG}
                                alt="We Meet We Eat fellowship gathering"
                                className="w-full aspect-[4/5] object-cover rounded-sm"
                            />
                            <div className="absolute -bottom-5 -right-5 bg-sanct-rose text-sanct-cream px-6 py-4 rounded-sm">
                                <p className="font-serif italic text-xl">A Sacred Fellowship</p>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">
                            <span className="inline-block w-8 h-px mr-3 align-middle bg-sanct-rose" />
                            Sacred Work
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-[64px] text-sanct-cocoa leading-[1.02] tracking-tight">
                            A fellowship for women who desire
                            <span className="block italic text-sanct-rose">deeper connection.</span>
                        </h2>
                        <p className="font-sans text-base md:text-lg text-sanct-charcoal/85 mt-8 leading-[1.8] max-w-xl">
                            Alongside our institutional work, <span className="italic">We Meet We Eat&reg;</span> is the spiritual assignment that began it all — a sanctuary, a sisterhood, and a God-led community for women walking in purpose, connection, and spiritual alignment.
                        </p>
                        <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa mt-6 leading-[1.5]">
                            This is where the work becomes sacred.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/wmwe"
                                data-testid="wmwe-explore-btn"
                                className="group inline-flex items-center gap-3 px-8 py-4 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa transition-colors duration-300"
                            >
                                <Heart size={14} />
                                Explore WMWE
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to="/wmwe/prayer"
                                className="inline-flex items-center gap-3 px-8 py-4 border border-sanct-cocoa text-sanct-cocoa text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa hover:text-sanct-cream transition-colors duration-300"
                            >
                                Visit the Prayer Room
                            </Link>
                        </div>
                    </div>
                </div>
            </section>


            {/* FINAL CTA */}
            <section
                className="relative py-28 md:py-40 overflow-hidden"
                style={{
                    backgroundImage: `linear-gradient(rgba(12, 15, 61, 0.85), rgba(12, 15, 61, 0.85)), url(${CONNECTION_IMG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
                data-testid="final-cta-section"
            >
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center text-[#fbf9f8]">
                    <Sparkles size={32} className="mx-auto text-[#ffb5ab] mb-6" />
                    <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tight">
                        Ready to architect a <span className="italic text-[#ffb5ab]">connection-centered</span> culture?
                    </h2>
                    <p className="font-sans text-base md:text-lg text-[#e3c7b9]/80 max-w-2xl mx-auto mt-8 leading-relaxed">
                        Whether you lead a university or a community &mdash; we&apos;ll help you build the relational infrastructure that strengthens culture, accelerates growth, and transforms how people work, learn, and lead.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4 justify-center">
                        <Link
                            to={ACUITY}
                            data-testid="cta-book-call-btn"
                            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#ffb5ab] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#fbf9f8] transition-colors duration-300"
                        >
                            Book a Strategy Call
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="cta-contact-btn"
                            className="inline-flex items-center gap-3 px-8 py-4 border border-[#fbf9f8] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#fbf9f8] hover:text-[#0c0f3d] transition-colors duration-300"
                        >
                            Send an Inquiry
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
